import React, { useRef, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Button, CircularProgress, Dialog, LinearProgress, TextField } from "@material-ui/core";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";
import CropIcon from "@material-ui/icons/Crop";
import VideocamOutlinedIcon from "@material-ui/icons/VideocamOutlined";
import LinkIcon from "@material-ui/icons/Link";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import EmojiObjectsRounded from "@material-ui/icons/EmojiObjectsRounded";
import NewReleasesRounded from "@material-ui/icons/NewReleasesRounded";
import WarningRounded from "@material-ui/icons/WarningRounded";
import BlockRounded from "@material-ui/icons/BlockRounded";
import CheckCircleRounded from "@material-ui/icons/CheckCircleRounded";
import CancelRounded from "@material-ui/icons/CancelRounded";
import CheckRounded from "@material-ui/icons/CheckRounded";
import CloseRounded from "@material-ui/icons/CloseRounded";
import TitleRounded from "@material-ui/icons/TitleRounded";
import NotesRounded from "@material-ui/icons/NotesRounded";
import ThumbsUpDownRounded from "@material-ui/icons/ThumbsUpDownRounded";
import FormatListNumberedRounded from "@material-ui/icons/FormatListNumberedRounded";
import ImageRounded from "@material-ui/icons/ImageRounded";
import VideocamRounded from "@material-ui/icons/VideocamRounded";
import RemoveRounded from "@material-ui/icons/RemoveRounded";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import EditorDeImagem from "../../components/EditorDeImagem";
import EditorTexto from "./EditorTexto";
import { linkDeVideo, sanitizarHtml } from "../../utils/regrasHtml";
import { VERDE, campoNavy } from "../../utils/estiloNavy";

export const urlPublica = (arquivo) => (arquivo ? `${getBackendUrl()}public/${arquivo}` : "");

// Tipos de destaque (caixas coloridas)
export const DESTAQUES = {
  dica: { rotulo: "Dica", Icone: EmojiObjectsRounded, cor: "#0ea5e9" },
  importante: { rotulo: "Importante", Icone: NewReleasesRounded, cor: "#8b5cf6" },
  atencao: { rotulo: "Atenção", Icone: WarningRounded, cor: "#f59e0b" },
  proibido: { rotulo: "Proibido", Icone: BlockRounded, cor: "#ef4444" },
  certo: { rotulo: "Faça assim", Icone: CheckCircleRounded, cor: "#22c55e" },
};

// Blocos que o admin pode adicionar
export const TIPOS = [
  { tipo: "titulo", rotulo: "Título", Icone: TitleRounded },
  { tipo: "texto", rotulo: "Texto", Icone: NotesRounded },
  { tipo: "destaque", rotulo: "Destaque", Icone: EmojiObjectsRounded },
  { tipo: "podeNaoPode", rotulo: "Pode / Não pode", Icone: ThumbsUpDownRounded },
  { tipo: "passos", rotulo: "Passo a passo", Icone: FormatListNumberedRounded },
  { tipo: "imagem", rotulo: "Foto", Icone: ImageRounded },
  { tipo: "video", rotulo: "Vídeo", Icone: VideocamRounded },
  { tipo: "divisor", rotulo: "Divisor", Icone: RemoveRounded },
];

export const novoBloco = (tipo) => {
  const id = Math.random().toString(36).slice(2, 10);
  switch (tipo) {
    case "titulo":
      return { id, tipo, texto: "" };
    case "texto":
      return { id, tipo, html: "" };
    case "destaque":
      return { id, tipo, estilo: "dica", titulo: "", html: "" };
    case "podeNaoPode":
      return { id, tipo, pode: [], naoPode: [] };
    case "passos":
      return { id, tipo, itens: [] };
    case "imagem":
      return { id, tipo, arquivo: "", legenda: "", tamanho: "m" };
    case "video":
      return { id, tipo, arquivo: "", url: "", legenda: "" };
    default:
      return { id, tipo: "divisor" };
  }
};

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(255,255,255,.09)" : "#e2e8f0";
  return {
    titulo: {
      fontSize: "1.25rem",
      fontWeight: 800,
      margin: "6px 0 2px",
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 10,
      "&::before": {
        content: '""',
        width: 6,
        height: 22,
        borderRadius: 4,
        background: "var(--rg-cor, #1E9855)",
      },
    },
    texto: {
      fontSize: "0.97rem",
      lineHeight: 1.7,
      color: theme.palette.text.primary,
      wordBreak: "break-word",
      "& ul, & ol": { margin: "4px 0", paddingLeft: 24 },
      "& p, & div": { margin: 0 },
    },
    destaque: {
      position: "relative",
      borderRadius: 14,
      padding: "14px 16px 14px 18px",
      display: "flex",
      gap: 12,
      overflow: "hidden",
    },
    destaqueIcone: { display: "flex", "& svg": { fontSize: 26 } },
    destaqueTitulo: { fontWeight: 800, fontSize: "0.95rem", marginBottom: 2 },
    duasColunas: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    coluna: { borderRadius: 14, padding: 14 },
    colunaTitulo: { fontWeight: 800, fontSize: "0.85rem", marginBottom: 8, display: "flex", gap: 6, alignItems: "center" },
    item: { display: "flex", gap: 8, padding: "4px 0", fontSize: "0.93rem", lineHeight: 1.5 },
    passos: { display: "flex", flexDirection: "column", gap: 8 },
    passo: {
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      padding: "10px 12px",
      borderRadius: 12,
      background: escuro ? "rgba(255,255,255,.03)" : "#f8fafc",
      border: `1px solid ${borda}`,
    },
    passoNum: {
      width: 26,
      height: 26,
      borderRadius: 8,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      fontSize: "0.8rem",
      color: "#fff",
      background: `linear-gradient(135deg, #34d399, ${VERDE})`,
    },
    figura: { margin: 0, textAlign: "center" },
    imagem: {
      maxWidth: "100%",
      borderRadius: 14,
      cursor: "zoom-in",
      boxShadow: "0 10px 28px rgba(2,6,23,.18)",
      border: `1px solid ${borda}`,
    },
    legenda: { fontSize: "0.82rem", color: theme.palette.text.secondary, marginTop: 6 },
    video: {
      width: "100%",
      aspectRatio: "16 / 9",
      borderRadius: 14,
      border: "none",
      background: "#000",
      boxShadow: "0 10px 28px rgba(2,6,23,.18)",
    },
    divisor: {
      border: "none",
      height: 2,
      margin: "8px 0",
      background: `linear-gradient(90deg, transparent, ${escuro ? "rgba(255,255,255,.18)" : "#cbd5e1"}, transparent)`,
    },

    // ── edição ──
    campo: { ...campoNavy(theme), width: "100%" },
    linha: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
    chip: {
      border: `1px solid ${borda}`,
      borderRadius: 10,
      padding: "6px 10px",
      background: "transparent",
      color: theme.palette.text.primary,
      fontFamily: "inherit",
      fontWeight: 700,
      fontSize: "0.8rem",
      cursor: "pointer",
      display: "inline-flex",
      gap: 6,
      alignItems: "center",
    },
    chipAtivo: { borderColor: "currentColor", boxShadow: "0 0 0 3px rgba(30,152,85,.12)" },
    vazioMidia: {
      border: `2px dashed ${borda}`,
      borderRadius: 14,
      padding: 20,
      textAlign: "center",
      color: theme.palette.text.secondary,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      alignItems: "center",
    },
    previa: { position: "relative", display: "inline-block", maxWidth: "100%" },
    zoom: { maxWidth: "92vw", maxHeight: "88vh", display: "block" },
  };
});

const fundoDe = (cor, escuro) => ({
  background: `linear-gradient(135deg, ${cor}${escuro ? "26" : "14"}, ${cor}${escuro ? "0f" : "08"})`,
  border: `1px solid ${cor}${escuro ? "55" : "40"}`,
  borderLeft: `5px solid ${cor}`,
});

// ───────────────────────── VISUALIZAÇÃO ─────────────────────────

export const BlocoVisual = ({ bloco }) => {
  const classes = useStyles();
  const escuro = useTheme().palette.type === "dark";
  const [zoom, setZoom] = useState(false);

  switch (bloco.tipo) {
    case "titulo":
      return bloco.texto ? <h3 className={classes.titulo}>{bloco.texto}</h3> : null;

    case "texto":
      return (
        <div className={classes.texto} dangerouslySetInnerHTML={{ __html: sanitizarHtml(bloco.html) }} />
      );

    case "destaque": {
      const d = DESTAQUES[bloco.estilo] || DESTAQUES.dica;
      return (
        <div className={classes.destaque} style={fundoDe(d.cor, escuro)}>
          <span className={classes.destaqueIcone} style={{ color: d.cor }}>
            <d.Icone />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.destaqueTitulo} style={{ color: d.cor }}>
              {bloco.titulo || d.rotulo}
            </div>
            <div className={classes.texto} dangerouslySetInnerHTML={{ __html: sanitizarHtml(bloco.html) }} />
          </div>
        </div>
      );
    }

    case "podeNaoPode":
      return (
        <div className={classes.duasColunas}>
          <div className={classes.coluna} style={fundoDe("#22c55e", escuro)}>
            <div className={classes.colunaTitulo} style={{ color: "#16a34a" }}>
              <CheckCircleRounded fontSize="small" /> Pode / Faça
            </div>
            {(bloco.pode || []).map((t, i) => (
              <div key={i} className={classes.item}>
                <CheckRounded fontSize="small" style={{ color: "#16a34a", flexShrink: 0, marginTop: 2 }} />
                <span>{t}</span>
              </div>
            ))}
          </div>
          <div className={classes.coluna} style={fundoDe("#ef4444", escuro)}>
            <div className={classes.colunaTitulo} style={{ color: "#dc2626" }}>
              <CancelRounded fontSize="small" /> Não pode / Nunca faça
            </div>
            {(bloco.naoPode || []).map((t, i) => (
              <div key={i} className={classes.item}>
                <CloseRounded fontSize="small" style={{ color: "#dc2626", flexShrink: 0, marginTop: 2 }} />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "passos":
      return (
        <div className={classes.passos}>
          {(bloco.itens || []).map((t, i) => (
            <div key={i} className={classes.passo}>
              <span className={classes.passoNum}>{i + 1}</span>
              <span style={{ paddingTop: 3 }}>{t}</span>
            </div>
          ))}
        </div>
      );

    case "imagem": {
      if (!bloco.arquivo) return null;
      const largura = { p: 320, m: 560, g: "100%" }[bloco.tamanho || "m"];
      return (
        <figure className={classes.figura}>
          <img
            src={urlPublica(bloco.arquivo)}
            alt={bloco.legenda || "Imagem"}
            className={classes.imagem}
            style={{ width: largura, maxWidth: "100%" }}
            onClick={() => setZoom(true)}
            loading="lazy"
          />
          {bloco.legenda && <figcaption className={classes.legenda}>{bloco.legenda}</figcaption>}
          <Dialog open={zoom} onClose={() => setZoom(false)} maxWidth={false}>
            <img src={urlPublica(bloco.arquivo)} alt="" className={classes.zoom} onClick={() => setZoom(false)} />
          </Dialog>
        </figure>
      );
    }

    case "video": {
      const link = bloco.arquivo ? { tipo: "arquivo", src: urlPublica(bloco.arquivo) } : linkDeVideo(bloco.url);
      if (!link) return null;
      return (
        <figure className={classes.figura}>
          {link.tipo === "youtube" ? (
            <iframe
              className={classes.video}
              src={link.src}
              title={bloco.legenda || "Vídeo"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video className={classes.video} src={link.src} controls preload="metadata" />
          )}
          {bloco.legenda && <figcaption className={classes.legenda}>{bloco.legenda}</figcaption>}
        </figure>
      );
    }

    case "divisor":
      return <hr className={classes.divisor} />;

    default:
      return null;
  }
};

// ───────────────────────── EDIÇÃO ─────────────────────────

const linhas = (lista) => (lista || []).join("\n");
const paraLista = (txt) =>
  String(txt || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

const enviarArquivo = async (arquivo, aoProgresso) => {
  const corpo = new FormData();
  corpo.append("arquivo", arquivo);
  const { data } = await api.post("/regras/upload", corpo, {
    onUploadProgress: (e) => e.total && aoProgresso && aoProgresso(Math.round((e.loaded * 100) / e.total)),
  });
  return data.filename;
};

const EditorImagem = ({ bloco, mudar }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [paraEditar, setParaEditar] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const escolher = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Escolha uma foto.");
    setParaEditar(f);
  };

  const editarAtual = async () => {
    try {
      const { data, headers } = await api.get(`/public/${bloco.arquivo}`, { responseType: "blob" });
      setParaEditar(new File([data], bloco.arquivo, { type: headers["content-type"] || "image/jpeg" }));
    } catch (err) {
      toastError(err);
    }
  };

  const confirmar = async (final) => {
    setParaEditar(null);
    setEnviando(true);
    try {
      const nome = await enviarArquivo(final);
      mudar({ arquivo: nome });
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={escolher} />
      {paraEditar && (
        <EditorDeImagem arquivo={paraEditar} open onCancel={() => setParaEditar(null)} onConfirmar={confirmar} />
      )}
      {bloco.arquivo ? (
        <div style={{ textAlign: "center" }}>
          <img src={urlPublica(bloco.arquivo)} alt="" style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 12 }} />
        </div>
      ) : (
        <div className={classes.vazioMidia}>
          <PhotoCameraOutlinedIcon />
          Escolha uma foto. Antes de enviar dá para cortar, girar e dar zoom.
        </div>
      )}
      {enviando && <LinearProgress />}
      <div className={classes.linha}>
        <Button size="small" variant="outlined" startIcon={<PhotoCameraOutlinedIcon />} onClick={() => inputRef.current?.click()} disabled={enviando}>
          {bloco.arquivo ? "Trocar foto" : "Escolher foto"}
        </Button>
        {bloco.arquivo && (
          <Button size="small" variant="outlined" startIcon={<CropIcon />} onClick={editarAtual} disabled={enviando}>
            Editar / cortar
          </Button>
        )}
        <span style={{ fontSize: "0.8rem", opacity: 0.7, marginLeft: 6 }}>Tamanho:</span>
        {[
          ["p", "Pequena"],
          ["m", "Média"],
          ["g", "Inteira"],
        ].map(([v, r]) => (
          <button
            key={v}
            type="button"
            className={`${classes.chip} ${bloco.tamanho === v ? classes.chipAtivo : ""}`}
            style={bloco.tamanho === v ? { color: VERDE } : undefined}
            onClick={() => mudar({ tamanho: v })}
          >
            {r}
          </button>
        ))}
      </div>
      <TextField
        className={classes.campo}
        size="small"
        variant="outlined"
        label="Legenda (opcional)"
        value={bloco.legenda || ""}
        onChange={(e) => mudar({ legenda: e.target.value })}
      />
    </div>
  );
};

const EditorVideo = ({ bloco, mudar }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [progresso, setProgresso] = useState(null);

  const escolher = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Escolha um arquivo de vídeo.");
    if (f.size > 100 * 1024 * 1024) return toast.error("Vídeo maior que 100 MB. Use um link do YouTube.");
    setProgresso(0);
    try {
      const nome = await enviarArquivo(f, setProgresso);
      mudar({ arquivo: nome, url: "" });
    } catch (err) {
      toastError(err);
    } finally {
      setProgresso(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input ref={inputRef} type="file" accept="video/*" hidden onChange={escolher} />
      {bloco.arquivo || linkDeVideo(bloco.url) ? (
        <BlocoVisual bloco={{ ...bloco, legenda: "" }} />
      ) : (
        <div className={classes.vazioMidia}>
          <VideocamOutlinedIcon />
          Envie um vídeo do computador (até 100 MB) ou cole um link do YouTube.
        </div>
      )}
      {progresso !== null && (
        <div>
          <LinearProgress variant="determinate" value={progresso} />
          <div style={{ fontSize: "0.75rem", marginTop: 4 }}>Enviando... {progresso}%</div>
        </div>
      )}
      <div className={classes.linha}>
        <Button
          size="small"
          variant="outlined"
          startIcon={progresso !== null ? <CircularProgress size={14} /> : <VideocamOutlinedIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={progresso !== null}
        >
          Enviar vídeo
        </Button>
        {bloco.arquivo && (
          <Button size="small" startIcon={<CloseIcon />} onClick={() => mudar({ arquivo: "" })}>
            Tirar vídeo
          </Button>
        )}
      </div>
      {!bloco.arquivo && (
        <TextField
          className={classes.campo}
          size="small"
          variant="outlined"
          label="Ou cole o link (YouTube, Vimeo ou .mp4)"
          value={bloco.url || ""}
          onChange={(e) => mudar({ url: e.target.value })}
          InputProps={{ startAdornment: <LinkIcon style={{ marginRight: 6, opacity: 0.6 }} /> }}
        />
      )}
      <TextField
        className={classes.campo}
        size="small"
        variant="outlined"
        label="Legenda (opcional)"
        value={bloco.legenda || ""}
        onChange={(e) => mudar({ legenda: e.target.value })}
      />
    </div>
  );
};

export const BlocoEditor = ({ bloco, mudar }) => {
  const classes = useStyles();

  switch (bloco.tipo) {
    case "titulo":
      return (
        <TextField
          className={classes.campo}
          variant="outlined"
          size="small"
          placeholder="Título do trecho (ex.: Como falar com o cliente)"
          value={bloco.texto || ""}
          onChange={(e) => mudar({ texto: e.target.value })}
          inputProps={{ style: { fontWeight: 800, fontSize: "1.05rem" } }}
        />
      );

    case "texto":
      return <EditorTexto valor={bloco.html} onChange={(html) => mudar({ html })} />;

    case "destaque":
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className={classes.linha}>
            {Object.entries(DESTAQUES).map(([chave, d]) => (
              <button
                key={chave}
                type="button"
                className={`${classes.chip} ${bloco.estilo === chave ? classes.chipAtivo : ""}`}
                style={bloco.estilo === chave ? { color: d.cor, background: `${d.cor}14` } : undefined}
                onClick={() => mudar({ estilo: chave })}
              >
                <d.Icone fontSize="small" style={{ color: d.cor }} /> {d.rotulo}
              </button>
            ))}
          </div>
          <TextField
            className={classes.campo}
            variant="outlined"
            size="small"
            label="Título do destaque (opcional)"
            value={bloco.titulo || ""}
            onChange={(e) => mudar({ titulo: e.target.value })}
          />
          <EditorTexto valor={bloco.html} onChange={(html) => mudar({ html })} placeholder="O que a equipe precisa saber..." />
        </div>
      );

    case "podeNaoPode":
      return (
        <div className={classes.duasColunas}>
          <TextField
            className={classes.campo}
            variant="outlined"
            multiline
            minRows={4}
            label="Pode (uma regra por linha)"
            value={linhas(bloco.pode)}
            onChange={(e) => mudar({ pode: e.target.value.split("\n") })}
            onBlur={(e) => mudar({ pode: paraLista(e.target.value) })}
          />
          <TextField
            className={classes.campo}
            variant="outlined"
            multiline
            minRows={4}
            label="Não pode (uma regra por linha)"
            value={linhas(bloco.naoPode)}
            onChange={(e) => mudar({ naoPode: e.target.value.split("\n") })}
            onBlur={(e) => mudar({ naoPode: paraLista(e.target.value) })}
          />
        </div>
      );

    case "passos":
      return (
        <TextField
          className={classes.campo}
          variant="outlined"
          multiline
          minRows={4}
          label="Passos (um por linha, na ordem)"
          value={linhas(bloco.itens)}
          onChange={(e) => mudar({ itens: e.target.value.split("\n") })}
          onBlur={(e) => mudar({ itens: paraLista(e.target.value) })}
        />
      );

    case "imagem":
      return <EditorImagem bloco={bloco} mudar={mudar} />;

    case "video":
      return <EditorVideo bloco={bloco} mudar={mudar} />;

    case "divisor":
      return <BlocoVisual bloco={bloco} />;

    default:
      return null;
  }
};

